import { useTranslations } from '@vocab/react';
import axios from 'axios';
import { Box, Stack } from 'braid-design-system';
import React, {
  forwardRef,
  type ReactElement,
  useContext,
  useEffect,
  useImperativeHandle,
  useReducer,
  useState,
} from 'react';

import translations from '@seek/rr-caja/src/.vocab';

import type { RoleRequirementsEvent } from '../../analytics';
import { useGenerateNewRecommendationsOnChange } from '../../hooks';
import { saveDraftError, saveError } from '../../saveError';
import {
  getQuestionnaire,
  getRecommendations,
  type JobDetails,
  type Question,
  saveDraftSelectedQuestions,
  saveQuestionnaire,
  type SelectedQuestion,
} from '../../sdk';
import { VersionMismatchError } from '../../sdk/apiError';
import { useConfig } from '../Config/ConfigProvider';
import {
  CustomQuestions,
  type CustomQuestionsWidget,
} from '../CustomQuestions/CustomQuestions';
import {
  IllegalCustomQuestions,
  CustomQuestionApiError,
} from '../CustomQuestions/operations';
import {
  isCompleteCustomQuestion,
  isCustomQuestionApiError,
  isIllegalCustomQuestion,
} from '../CustomQuestions/types';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import { Header } from '../Header/Header';
import { useLog } from '../Log/LogProvider';
import { Questions } from '../Questions/Questions';
import { ReadOnlyQuestions } from '../ReadOnlyQuestions/ReadOnlyQuestions';
import { AnalyticsContext } from '../RoleRequirements/RoleRequirements';
import type { ValidationResult, Widget } from '../types';
import { questionPanelId, questionsUpdatedId } from '../uiErrors';

import {
  getSelectedQuestions,
  getVersionNumber,
  initialState,
  isInErrorState,
  questionsInError,
  reducer,
  setQuestions,
  setVersionNumber,
  getQuestions,
  getAllSelectedQuestions,
  setQuestionnaireHasQuestions,
  isVBQInError,
} from './state';

/** Render Prop for customising how the Questions tile is rendered */
export type RenderQuestionsTile = (children: React.ReactNode) => ReactElement;

interface Props {
  questionnaireId: string;
  jobDetails?: JobDetails;
  onError?: (message: string, metadata?: any) => void;
  onChange?: (selections: SelectedQuestion[]) => void;
  onEvent?: (event: RoleRequirementsEvent) => void;
  renderQuestions: RenderQuestionsTile;
  onSelectedQuestionsChange?: (questions: string[]) => void;
  /** Optional: localised help message to show users when an error panel displays */
  helpInformation?: React.ReactNode;
  isExperimentLoaded: boolean;
}

const App: React.ForwardRefRenderFunction<Widget, Props> = (
  {
    questionnaireId,
    jobDetails,
    renderQuestions,
    onSelectedQuestionsChange,
    isExperimentLoaded,
    ...props
  },
  ref,
) => {
  if (!questionnaireId) {
    throw new Error('Missing questionnaireId');
  }

  const [, setState] = useState(); // https://github.com/facebook/react/issues/14981#issuecomment-468460187
  const { t } = useTranslations(translations);
  const preferredAnswersErrorMessage = t(
    'error.questions.select-preferred-answers',
  );
  const unableToSaveErrorMessage = t('error.widget.UnableToSave');
  const [state, dispatch] = useReducer(reducer, initialState);
  const customQuestionRef = React.createRef<CustomQuestionsWidget>();
  const [showReviewQuestions, setShowReviewQuestions] = useState(false);
  const [displayValidation, setDisplayValidation] = useState<boolean>(false);
  const [initComplete, setInitComplete] = useState(false);
  const [isVerifyBoosted, setIsVerifyBoosted] = useState<
    boolean | undefined | null
  >(false);
  const [verifyBoostError, setVerifyBoostError] = useState<boolean>(false);
  const [draftCustomQuestionTexts, setDraftCustomQuestionTexts] = useState<
    string[]
  >([]);
  const analytics = useContext(AnalyticsContext);

  const [fetchedQuestions, setFetchedQuestions] = useState<Question[]>([]);
  const [fetchQuestionnaireState, setFetchQuestionnaireState] = useState<
    'loading' | 'error' | 'questions'
  >('loading');

  const config = useConfig();
  const { onError } = useLog();

  useGenerateNewRecommendationsOnChange(
    questionnaireId,
    initComplete,
    state,
    dispatch,
    jobDetails,
  );

  useEffect(() => {
    if (isExperimentLoaded) {
      const source = axios.CancelToken.source();

      const fetchRecommendations = async () => {
        try {
          const response = await getRecommendations(
            questionnaireId,
            config,
            source.token,
          );
          dispatch(setQuestions(response));
          setIsVerifyBoosted(response.isVerifyBoosted);
          setInitComplete(true);
        } catch (error) {
          setState(() => {
            throw error;
          });
        }
      };

      fetchRecommendations();

      return () => {
        if (source) {
          source.cancel('Cleanup');
        }
      };
    }
    // exclude `config` from deps which causes infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireId, isExperimentLoaded]);

  useEffect(() => {
    if (onSelectedQuestionsChange) {
      onSelectedQuestionsChange(
        getAllSelectedQuestions(state.questions)
          .map((question) => question.text)
          .concat(draftCustomQuestionTexts),
      );
    }
  }, [draftCustomQuestionTexts, onSelectedQuestionsChange, state.questions]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    const fetchQuestionnaire = async () => {
      try {
        const response = await getQuestionnaire(questionnaireId, source.token);
        dispatch(
          setQuestionnaireHasQuestions(
            (response.questions && response.questions.length > 0) ?? false,
          ),
        );
        setIsVerifyBoosted(response.isVerifyBoosted);
        setFetchedQuestions(response.questions ?? []);
        setFetchQuestionnaireState('questions');
      } catch (error) {
        setFetchedQuestions([]);
        setFetchQuestionnaireState('error');
        onError?.('rr-caja | Failed to fetch questionnaire', {
          error,
          questionnaireId,
        });
      }
    };
    if (state.published && isExperimentLoaded) {
      fetchQuestionnaire();
    }

    return () => {
      source.cancel('Cleanup');
    };
  }, [questionnaireId, state.published, dispatch, isExperimentLoaded, onError]);

  const valid = (): ValidationResult => {
    if (isInErrorState(state)) {
      setDisplayValidation(true);
      analytics.sendPreferredAnswerNotSelectedEvent(
        t('error.question.select-preferred-answers'),
      );
      return {
        valid: false,
        errorIdentifier: questionsInError(state).map(questionPanelId),
      };
    }

    if (isVBQInError(state, isVerifyBoosted ?? false)) {
      setVerifyBoostError(true);
      analytics.sendVerifyBoostNoQuestionSelectedEvent(
        questionnaireId,
        'Select at least one (purple tick icon) question',
      );
      setDisplayValidation(true);
      return { valid: false, errorIdentifier: ['verifyBoostWidget'] };
    }

    setDisplayValidation(false);
    return { valid: true };
  };

  const saveDraft = async () => {
    if (!initComplete) {
      return;
    }

    if (state.published === true) {
      return;
    }

    setShowReviewQuestions(false);

    try {
      const customQuestions = (await customQuestionRef.current?.save()) ?? [];
      const { selectedQuestions } = getSelectedQuestions(state);

      const newVersion = await saveDraftSelectedQuestions({
        questionnaireId,
        selectedQuestions: [
          ...selectedQuestions,
          ...customQuestions.filter(isCompleteCustomQuestion),
        ],
        versionNumber: getVersionNumber(state),
        cancelToken: axios.CancelToken.source().token,
        isVerifyBoosted: isVerifyBoosted === null ? undefined : isVerifyBoosted,
      });

      dispatch(setVersionNumber(newVersion));
    } catch (err) {
      onError?.('rr-caja | Failed to save draft', {
        error: err,
        questionnaireId,
      });

      if (err instanceof VersionMismatchError) {
        reloadQuestionnaireState();

        throw saveDraftError('Draft save failed.', {
          errorIdentifier: [questionsUpdatedId],
        });
      }

      throw saveDraftError('Draft save failed.');
    }
  };

  const save = async () => {
    if (!initComplete) {
      return [];
    }

    if (state.published === true) {
      return [];
    }

    setShowReviewQuestions(false);

    const validState = valid();
    if (!validState.valid) {
      if (validState.errorIdentifier.includes('verifyBoostWidget')) {
        scrollTo({
          behavior: 'smooth',
          top: 0,
        });
        throw saveError('Please select VB question')({ blocksUserFlow: true })({
          errorIdentifier: validState.errorIdentifier,
        });
      } else {
        throw saveError(preferredAnswersErrorMessage)({ blocksUserFlow: true })(
          {
            errorIdentifier: validState.errorIdentifier,
          },
        );
      }
    }

    try {
      const customQuestions = (await customQuestionRef.current?.save()) ?? [];
      if (customQuestions.some(isIllegalCustomQuestion)) {
        throw new IllegalCustomQuestions(
          customQuestions.filter(isIllegalCustomQuestion),
        );
      }

      const { selectedQuestions } = getSelectedQuestions(state);

      const response = await saveQuestionnaire({
        questionnaireId,
        versionNumber: getVersionNumber(state),
        isVerifyBoosted: isVerifyBoosted === null ? undefined : isVerifyBoosted,
        selectedQuestions: [
          ...selectedQuestions,
          ...customQuestions.filter(isCompleteCustomQuestion),
        ],
        cancelToken: axios.CancelToken.source().token,
      });

      const newVersion = response.etag;
      const updatedQuestions = response.questions;

      dispatch(setVersionNumber(newVersion));

      if (customQuestions.some(isCustomQuestionApiError)) {
        throw new CustomQuestionApiError();
      }

      analytics.sendVerifyBoostContinueEvent(
        questionnaireId,
        isVerifyBoosted ?? false,
      );

      const updatedQuestionGlobalIds =
        updatedQuestions
          ?.map((q) => q.globalId)
          .filter<string>(
            (globalId): globalId is string => typeof globalId === 'string',
          ) ?? [];

      return updatedQuestionGlobalIds;
    } catch (err) {
      const unableToSaveError = saveError(unableToSaveErrorMessage);
      const blockingError = unableToSaveError({ blocksUserFlow: true });

      if (err instanceof IllegalCustomQuestions) {
        throw blockingError({
          body: t('error.questions.custom.illegalSave')?.toLocaleString(),
          errorIdentifier: err.illegalQuestions.map((q) => q.location),
        });
      }
      if (err instanceof VersionMismatchError) {
        reloadQuestionnaireState();
        throw blockingError({
          body: t('error.questions.updatedErrorMessage')?.toLocaleString(),
          errorIdentifier: [questionsUpdatedId],
        });
      }

      onError?.('rr-caja | Failed to update questionnaire', {
        error: err,
        questionnaireId,
      });

      const nonblockingError = unableToSaveError({ blocksUserFlow: false });

      if (err instanceof CustomQuestionApiError) {
        throw nonblockingError({
          body: t('error.questions.custom.apiErrorSave')?.toLocaleString(),
        });
      }

      throw nonblockingError({
        body: t('error.questions.UnableToSaveMessage')?.toLocaleString(),
      });
    }
  };

  useImperativeHandle(ref, () => ({
    valid,
    saveDraft,
    save,
  }));

  const reloadQuestionnaireState = () => {
    setShowReviewQuestions(true);

    getRecommendations(
      questionnaireId,
      config,
      axios.CancelToken.source().token,
    )
      .then((rec) => {
        dispatch(setQuestions(rec));
      })
      .catch((error) => {
        setState(() => {
          throw error;
        });
      });
  };
  /**
   * Surround the whole widget to prevent an unintentional form submit
   * on its clients (e.g: express-caja-ui has its own parent form component
   * which will invoke a navigation to next page when pressing enter on an input field)
   */
  return (
    <div
      onKeyPress={(e) => {
        /**
         * Only doing it for an input text / search element
         * because those are the two that will invoke on submit handler
         * on a form.
         */
        if (
          e.key === 'Enter' &&
          e.target instanceof HTMLInputElement &&
          ['text', 'search'].includes(e.target.type)
        ) {
          e.preventDefault();
        }
      }}
    >
      <Stack space="large">
        {renderQuestions(
          <Stack space="large">
            <Header {...state} />
            {state.published ? (
              <ReadOnlyQuestions
                fetchedQuestions={fetchedQuestions}
                fetchQuestionnaireState={fetchQuestionnaireState}
              />
            ) : (
              <Questions
                questionnaireId={questionnaireId}
                state={state}
                dispatch={dispatch}
                showReviewQuestions={showReviewQuestions}
                displayValidation={displayValidation}
                setDisplayValidation={setDisplayValidation}
                isLoading={Boolean(jobDetails)}
                isVerifyBoosted={isVerifyBoosted ?? false}
                setIsVerifyBoosted={setIsVerifyBoosted}
                verifyBoostError={verifyBoostError}
                setVerifyBoostError={setVerifyBoostError}
                {...props}
              />
            )}
          </Stack>,
        )}
        {state.customQuestionsSelectionLimit > 0 && state.showQuestions && (
          <Box paddingBottom="medium">
            <ErrorBoundary questionnaireId={questionnaireId} {...props}>
              <CustomQuestions
                ref={customQuestionRef}
                questions={getQuestions(state)}
                customQuestionsSelectionLimit={
                  state.customQuestionsSelectionLimit
                }
                onDraftCustomQuestionsUpdated={setDraftCustomQuestionTexts}
                dispatch={dispatch}
                {...props}
              />
            </ErrorBoundary>
          </Box>
        )}
      </Stack>
    </div>
  );
};

const ForwardedRefApp = forwardRef(App);

export { ForwardedRefApp as Application };
